import Box from '@mui/material/Box';
import { Field, useFormikContext } from 'formik';
import { Trans, useTranslation } from 'react-i18next';

import { Divider } from 'common/components/Divider';
import { Switch, TextField } from 'common/components/FormField';
import { FormSection } from 'common/components/FormSection';
import { Link } from 'common/components/Link';
import { IContentTypeValue, IFeature } from 'graphql/types';
import { ExtraCategoryFormFields } from 'organisation/components/ExtraCategoryFormFields';
import { useFrontendPermissions } from 'user/hooks';
import { TrainingSelector } from 'training/components/FormField/TrainingSelector';
import { GroupSkillField } from 'training/components/GroupSkillField';
import { useFeature } from 'common/hooks/useFeature';
import { TGroupCreateFormValues, TGroupUpdateFormValues } from 'user/types';

interface IProps {
    isEditable?: boolean;
}

export const GroupGeneralForm = ({ isEditable }: IProps) => {
    const [translate] = useTranslation();
    const form = useFormikContext<
        TGroupCreateFormValues | TGroupUpdateFormValues
    >();
    const { canUpdate: canUpdateGroup } = useFrontendPermissions('group');
    const { canUse: canUseSkills } = useFeature(IFeature.Skill);

    const { values } = form;

    return (
        <>
            {isEditable && canUpdateGroup && (
                <>
                    <Field
                        component={TextField}
                        label={translate('name')}
                        name="name"
                    />

                    <Box sx={{ mt: canUpdateGroup ? 4 : 3 }}>
                        <Divider />
                    </Box>
                </>
            )}
            <FormSection
                defaultExpanded={!isEditable || (isEditable && !canUpdateGroup)}
                description={
                    <Box>
                        <Trans
                            components={{
                                a: <Link rel="noreferrer" underline="always" />,
                            }}
                            i18nKey="groupForm.trainingsDescription"
                        />
                    </Box>
                }
                title={translate('groupForm.trainings')}
            >
                <Box sx={{ mt: 2 }}>
                    <TrainingSelector
                        isEditable={canUpdateGroup}
                        label={translate('groupForm.addTrainings')}
                        name="trainingIds"
                        value={values.trainingIds || []}
                    />
                </Box>
            </FormSection>
            {isEditable && canUpdateGroup && canUseSkills && (
                <FormSection
                    renderExpanded
                    description={
                        <Box>
                            <Trans
                                components={{
                                    a: (
                                        <Link
                                            rel="noreferrer"
                                            underline="always"
                                        />
                                    ),
                                }}
                                i18nKey="groupForm.skillsDescription"
                            />
                        </Box>
                    }
                    title={translate('groupForm.skills')}
                >
                    <Box sx={{ mt: 2 }}>
                        <GroupSkillField
                            name="skills"
                            value={values.skills || []}
                        />
                    </Box>
                </FormSection>
            )}
            {isEditable && canUpdateGroup && (
                <>
                    <FormSection
                        description={
                            <Box>
                                <Trans
                                    components={{
                                        a: (
                                            <Link
                                                rel="noreferrer"
                                                underline="always"
                                            />
                                        ),
                                    }}
                                    i18nKey="groupForm.categoriesDescription"
                                />
                            </Box>
                        }
                        title={translate('groupForm.categories')}
                    >
                        <Box sx={{ mt: 2 }}>
                            <ExtraCategoryFormFields
                                contentType={IContentTypeValue.Group}
                            />
                        </Box>
                    </FormSection>

                    <FormSection
                        description={
                            <Box>
                                <Trans
                                    components={{
                                        a: (
                                            <Link
                                                rel="noreferrer"
                                                underline="always"
                                            />
                                        ),
                                    }}
                                    i18nKey="groupForm.settingsDescription"
                                />
                            </Box>
                        }
                        title={translate('groupForm.settings')}
                    >
                        <Box sx={{ mt: 2 }}>
                            <Field
                                component={TextField}
                                label={translate('groupForm.maxParticipants')}
                                name="maxUsers"
                            />

                            <Field
                                component={TextField}
                                helperText={
                                    <Trans
                                        components={{
                                            a: (
                                                <Link
                                                    rel="noreferrer"
                                                    underline="always"
                                                />
                                            ),
                                        }}
                                        i18nKey="groupForm.externalReferenceDescription"
                                    />
                                }
                                label={translate('groupForm.externalReference')}
                                name="ref"
                            />

                            <Field
                                checked={values.enableChat}
                                component={Switch}
                                helperText={translate(
                                    'groupForm.chatDescription'
                                )}
                                label={translate('groupForm.chat')}
                                name="enableChat"
                            />
                        </Box>
                    </FormSection>
                </>
            )}
        </>
    );
};
